import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../css/navbar.css";
import logo from "../images/resumify-logo.png";

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 1. Change `user` to be stateful so the UI re-renders when it changes
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user")),
  );

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  // 2. Add a check to see if the backend session is still alive
  useEffect(() => {
    const checkSession = async () => {
      // If there's no user in local storage, no need to check
      if (!localStorage.getItem("user")) return;

      try {
        const res = await fetch(`${API_URL}/api/auth/status`, {
          // Include credentials so the browser sends the session cookie
          credentials: "include",
        });

        if (res.status === 401 || res.status === 403) {
          // The backend says the session is expired/invalid
          localStorage.removeItem("user");
          setUser(null); // Re-renders the UI to show "Get Started"
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    };

    checkSession();
  }, [API_URL]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/logout`, {
        method: "POST",

        credentials: "include",
      });

      localStorage.removeItem("user");
      setUser(null); // 3. Update state immediately
      setDropdownOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleBuilderClick = async () => {
    setMobileMenuOpen(false);

    if (!user) {
      navigate("/builder");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/resumes/load`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();

        if (data.hasResume) {
          localStorage.setItem("resumeDraft", JSON.stringify(data.formData));
          navigate("/builder", {
            state: {
              isEdit: true,
              resumeId: data.id,
              title: data.title,
            },
          });
        } else {
          localStorage.removeItem("resumeDraft");
          navigate("/builder", { state: { isNew: true } });
        }
      } else {
        if (res.status === 401) {
          localStorage.removeItem("user");
          setUser(null);
        }
        navigate("/builder");
      }
    } catch (error) {
      console.error("Failed to load recent resume:", error);
      navigate("/builder");
    }
  };

  return (
    <nav className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}>
      <div className="nav-left">
        <div className="logo" onClick={() => navigate("/")}>
          <span className="logo-text">resumify</span>
        </div>

        {/* Nav Links sit right next to the logo now */}
        <div className="nav-links">
          <button onClick={handleBuilderClick}>Builder</button>
          <button onClick={() => navigate("/pricing")}>Pricing</button>
          <button onClick={() => navigate("/contactus")}>Contact Us</button>
          <button onClick={() => navigate("/tracker")}>Job Tracker</button>
          <button onClick={() => navigate("/interviewsimulator")}>
            Interview Simulator
          </button>
        </div>
      </div>

      <div className="nav-right">
        {user ? (
          <div className="user-menu" ref={dropdownRef}>
            <div
              className="avatar"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {getInitials(user.name)}
            </div>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <p className="dropdown-name">{user.name}</p>
                  <p className="dropdown-email">{user.email}</p>
                </div>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/dashboard");
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/account");
                  }}
                >
                  Account Settings
                </button>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="nav-cta" onClick={() => handleNavClick("/auth")}>
            Get Started
          </button>
        )}

        <button
          className={`hamburger ${mobileMenuOpen ? "open" : ""}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>
      </div>

      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <button onClick={handleBuilderClick}>Builder</button>
        <button onClick={() => handleNavClick("/pricing")}>Pricing</button>
        <button onClick={() => handleNavClick("/contactus")}>Contact Us</button>
        <button onClick={() => handleNavClick("/tracker")}>Job Tracker</button>
        <button onClick={() => handleNavClick("/interviewsimulator")}>
          Interview Simulator
        </button>
      </div>
    </nav>
  );
}
